(ns measure.reporting-ocp.db.options
  (:require [clojure.set :as set]
            [clojure.string :as str]
            [common.queries :refer [use-query]]
            [common.resource.query.adset :as adset-queries]
            [common.resource.query.campaign :as campaign-queries]
            [common.resource.query.user :as user-queries]
            [common.util :as util]
            [common.widgets.daterange-picker :refer [shortcuts-items]]
            [manage.adset.util :as adset-util]
            [measure.queries :as q]
            [uix.core :refer [$ defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(def multi-adset-report-types
  #{"custom" "vast_custom" "product_store"})

(def metric-report-types
  #{"custom" "vast_custom" "product_store"})

(def fields-report-types
  #{"custom" "vast_custom"})

(def product-fields-report-types
  #{"product_store"})

(def allowed-channels
  "Reporting support is limited to display, ctv and video channel adsets"
  #{"display" "video" "ctv"})

(def channel-options
  (->> allowed-channels
       (mapv (fn [allowed-channel]
               {:value allowed-channel
                :label (str/capitalize allowed-channel)
                :icon ($ adset-util/channel-icon {:key allowed-channel :channel allowed-channel})}))))

(def date-phrase-options
  (->> (shortcuts-items)
       (remove (comp #{"year_to_today"} :id))
       (mapv (fn [{:keys [id label]}]
               {:label label :value id}))))

(def format-options
  [{:label "Excel"
    :value "xlsx"}
   {:label "CSV"
    :value "csv"}])

(def schedule-options
  [{:label "No schedule"
    :value nil
    :id :none}
   {:label "Daily"
    :value "daily"
    :id :daily
    :tip "A report will be created everyday after midnight"}
   {:label "Weekly"
    :value "weekly"
    :id :weekly
    :tip "A report will be created every monday after midnight"}
   {:label "Monthly"
    :value "monthly"
    :id :montly
    :tip "A report will be created on the first day of the month after midnight"}])

(def ad-variant-counts-options
  [{:label "Impressions"
    :value ["impression"]}
   {:label "Impressions + Clicks"
    :value ["impression" "click"]}])

(defhook use-ad-variant-type-options []
  (let [adset-channel (use-subscribe [:form.report/value [:adset-channel]])]
    [{:label "Custom report"
      :value (if (= adset-channel "display")
               "custom"
               "vast_custom")}]))

(def timezone-options
  (map #(hash-map :label % :value %) util/timezones))

(defhook use-adset-options []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {adsets :data} (use-query (adset-queries/adsets-query {:advertiser-id advertiser-id}))
        {campaigns :data} (use-query (campaign-queries/campaigns-query {:advertiser-id advertiser-id}))
        adset-channel (use-subscribe [:form.report/value [:adset-channel]])

        campaigns* (mapv (fn [{:keys [name id]}]
                           {:campaign-name name
                            :campaign-id id}) campaigns)
        adsets-with-campaign-name (set/project
                                   (set/join adsets campaigns* {:campaignId :campaign-id})
                                   [:id :archived :name :channel :campaign-name :campaign-id])]
    (->> adsets-with-campaign-name
         (remove :archived)
         (keep (fn [{:keys [id name channel campaign-name campaign-id]}]
                 (when (= adset-channel channel)
                   (hash-map :label (str name " (" id ")")
                             :value id
                             :campaign (str campaign-name " (" campaign-id ")")
                             :campaignId campaign-id))))
         (sort-by :campaign))))

(defhook use-field-options [field-type]
  (let [adset-ids (use-subscribe [:form.report/value [:adset-ids]])
        {fields :data} (use-query (q/report-fields-query {:adset-ids adset-ids}))]
    (->> (get fields field-type)
         (map #(hash-map :label % :value %)))))

(defhook use-selected-adset-workflows []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        adset-ids (set (use-subscribe [:form.report/value [:adset-ids]]))
        {adsets :data} (use-query (adset-queries/adsets-query {:advertiser-id advertiser-id}))]
    (->> adsets
         (filter (comp adset-ids :id))
         (map :workflow)
         set)))

(defhook use-metric-options []
  (let [adset-channel (use-subscribe [:form.report/value [:adset-channel]])

        default-metrics ["click" {:label "impression" :value "impression" :locked true :disabled? true}]
        video-metrics ["resume" "pause" "rewind" "mute" "unmute" "fullscreen"
                       "start" "firstQuartile" "midpoint" "thirdQuartile" "complete"]]
    (cond->> default-metrics
      (not= adset-channel "display") (concat video-metrics)
      true (map (fn [option]
                  (if (map? option)
                    option
                    {:label option :value option}))))))

(defhook use-product-field-options []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        adset-ids (use-subscribe [:form.report/value [:adset-ids]])

        {product-fields :data} (use-query (q/report-product-fields-query {:advertiser-id advertiser-id :adset-ids adset-ids}))]
    (map #(hash-map :label % :value %) product-fields)))

(defhook use-type-options []
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {:keys [adset-ids adset-channel]} (use-subscribe [:form.report/value []])

        {adsets :data} (use-query (adset-queries/adsets-query {:advertiser-id advertiser-id}))

        product-fields (use-product-field-options)

        default-report-types [{:label "Context report"
                               :value "impression_context"
                               :tip "A report showing impressions and clicks for context used for dynamic content in the campaign"}]
        video-report-types [{:label "Content report"
                             :value "vast_adset_summary"
                             :tip "A report showing impressions and clicks for variation on content"}
                            {:label "Split Test report"
                             :value "vast_split_tests"
                             :tip "A report showing impressions and clicks for split tests"}
                            {:label "Custom report"
                             :value "vast_custom"
                             :tip "A report showing impressions and clicks based on selected content fields and metrics"}]
        display-report-types (cond-> [{:label "Content report"
                                       :value "adset_summary"
                                       :tip "A report showing impressions and clicks for variation on content"}
                                      {:label "Split Test report"
                                       :value "split_tests"
                                       :tip "A report showing impressions and clicks for split tests"}
                                      {:label "Custom report"
                                       :value "custom"
                                       :tip "A report showing impressions and clicks based on selected content and context fields"}]
                               (seq product-fields)
                               (conj (let [workflows (->> adsets
                                                          (keep (fn [adset]
                                                                  (when (contains? (set adset-ids) (:id adset))
                                                                    (:workflow adset))))
                                                          set)
                                           disabled? (contains? workflows "content-variants")]
                                       {:label "Product report"
                                        :value "product_store"
                                        :disabled? disabled?
                                        :tip (if disabled?
                                               "Product reports are not available for variant based adsets"
                                               "A report showing impressions and clicks for product fields")})))

        order ["adset_summary" "vast_adset_summary"
               "impression_context"
               "split_tests" "vast_split_tests"
               "product_store"
               "custom" "vast_custom"]
        report-type->idx (into {} (map-indexed (fn [i e] [e i]) order))]
    (cond->> default-report-types
      (not= adset-channel "display") (concat video-report-types)
      (= adset-channel "display") (concat display-report-types)
      (> (count adset-ids) 1) (filterv #(multi-adset-report-types (:value %)))
      true (sort-by (comp report-type->idx :value)))))

(defhook use-email-options []
  (let [{:keys [agency-id]} (use-subscribe [:url-context])
        {users :data} (use-query (user-queries/users-query {:agency-id agency-id}))]
    (map (fn [{:keys [email name]}]
           {:label name
            :value email})
         users)))
